import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Col, Container, Form, Row } from 'react-bootstrap';
import Select from 'react-select';
import moment from 'moment';
import { toast } from 'react-toastify';
import Header from '~/Components/Header/Header';
import Loading from '~/Components/Loading';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import { extrairMensagemErro } from '~/Utils/Helpers';
import {
  calcularJurosBaixa,
  listarDepartamentosBaixa,
  listarTitulosBaixa,
  listarBancosLista,
  listarContasPortador,
  listarMoedasContas,
  processarBaixaTitulo,
  simulacaoGetCedentes,
  simulacaoGetGruposEmpresa,
} from '~/Services/Service';
import CardFiltros from './components/CardFiltros';
import GradeTitulos from './components/GradeTitulos';
import PainelTotais from './components/PainelTotais';
import IntercambioFields from './components/IntercambioFields';
import ConfirmacaoBaixaModal from './components/ConfirmacaoBaixaModal';
import { filtrosSchema, baixaSchema } from './validationSchema';

const TAMANHO_PAGINA = 50;
const LIMITE_LOTE_TITULOS = 200;
const FORMATO_DATA_API = 'YYYY-MM-DD';

const normalizarResposta = (res) => (res && res.data ? res.data : res);

const filtrosIniciais = () => ({
  codigoGrupoEmpresa: '',
  dataVencimentoIni: moment().subtract(30, 'days').format(FORMATO_DATA_API),
  dataVencimentoFim: moment().format(FORMATO_DATA_API),
  codigoBanco: null,
  codigoPortador: null,
  codigoCedente: null,
  codigoAtivo: null,
  tipos: [],
  buscaRapida: '',
  campoBuscaRapida: 'numero',
});

const sortConfigInicial = { campo: 'DataVencimento', direcao: 'ASC' };

const construirPayloadListar = (filtros, paginaAtual, sortConfig) => ({
  codigoGrupoEmpresa: Number(filtros.codigoGrupoEmpresa),
  dataVencimentoIni: filtros.dataVencimentoIni,
  dataVencimentoFim: filtros.dataVencimentoFim,
  codigoBanco: filtros.codigoBanco != null ? Number(filtros.codigoBanco) : null,
  codigoPortador: filtros.codigoPortador != null ? Number(filtros.codigoPortador) : null,
  codigoCedente: filtros.codigoCedente != null ? Number(filtros.codigoCedente) : null,
  codigoAtivo: filtros.codigoAtivo != null ? Number(filtros.codigoAtivo) : null,
  tipos: filtros.tipos || [],
  buscaRapida: filtros.buscaRapida || null,
  campoBuscaRapida: filtros.buscaRapida ? filtros.campoBuscaRapida : null,
  pagina: paginaAtual,
  tamanhoPagina: TAMANHO_PAGINA,
  ordenacaoCampo: sortConfig.campo,
  ordenacaoDirecao: sortConfig.direcao,
});

const BaixaPorData = () => {
  const [filtros, setFiltros] = useState(filtrosIniciais());
  const [gruposEmpresa, setGruposEmpresa] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [portadores, setPortadores] = useState([]);
  const [cedentes, setCedentes] = useState([]);
  const [moedas, setMoedas] = useState([]);

  const [titulos, setTitulos] = useState([]);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [sortConfig, setSortConfig] = useState(sortConfigInicial);

  const [selecao, setSelecao] = useState(() => new Set());
  const [calculados, setCalculados] = useState(() => new Map());

  const [dataBaixa, setDataBaixa] = useState(moment().format(FORMATO_DATA_API));
  const [considerarJuros, setConsiderarJuros] = useState(true);
  const [codigoBancoBaixa, setCodigoBancoBaixa] = useState(null);
  const [intercambioAtivo, setIntercambioAtivo] = useState(false);
  const [intercambio, setIntercambio] = useState({
    codigoContaCredito: null,
    codigoContaDebito: null,
    codigoDepartamento: null,
  });
  const [departamentos, setDepartamentos] = useState([]);
  const [chContabil, setChContabil] = useState(false);
  const [dataContabil, setDataContabil] = useState(moment().format(FORMATO_DATA_API));

  const [carregando, setCarregando] = useState(false);
  const [calculandoJuros, setCalculandoJuros] = useState(false);
  const [processandoBaixa, setProcessandoBaixa] = useState(false);
  const [modalConfirmacao, setModalConfirmacao] = useState(false);

  useEffect(() => {
    let ativo = true;
    const carregar = async () => {
      try {
        const [resGes, resBancos, resMoedas] = await Promise.all([
          simulacaoGetGruposEmpresa(),
          listarBancosLista(),
          listarMoedasContas(),
        ]);
        if (!ativo) return;
        const ges = normalizarResposta(resGes) || [];
        setGruposEmpresa(ges);
        setBancos(normalizarResposta(resBancos) || []);
        setMoedas(normalizarResposta(resMoedas) || []);
        // Auto-seleção GE (W-0444/W-0445 pattern): se há 1 GE, fixa
        if (ges.length === 1) {
          setFiltros((f) => ({
            ...f,
            codigoGrupoEmpresa: ges[0].codigo_grupo_empresa || ges[0].codigo,
          }));
        }
      } catch (err) {
        if (!ativo) return;
        toast.error(extrairMensagemErro(err));
      }
    };
    carregar();
    return () => { ativo = false; };
  }, []);

  useEffect(() => {
    let ativo = true;
    const ge = filtros.codigoGrupoEmpresa;
    if (!ge) {
      setPortadores([]);
      setCedentes([]);
      return undefined;
    }
    const carregar = async () => {
      try {
        const [resPortadores, resCedentes] = await Promise.all([
          listarContasPortador(ge).catch(() => []),
          simulacaoGetCedentes(ge).catch(() => []),
        ]);
        if (!ativo) return;
        setPortadores(normalizarResposta(resPortadores) || []);
        setCedentes(normalizarResposta(resCedentes) || []);
      } catch (err) {
        if (!ativo) return;
        toast.error(extrairMensagemErro(err));
      }
    };
    carregar();
    return () => { ativo = false; };
  }, [filtros.codigoGrupoEmpresa]);

  useEffect(() => {
    if (!intercambioAtivo || !filtros.codigoGrupoEmpresa) {
      return undefined;
    }
    let ativo = true;
    listarDepartamentosBaixa(filtros.codigoGrupoEmpresa)
      .then((res) => {
        if (!ativo) return;
        setDepartamentos(normalizarResposta(res) || []);
      })
      .catch((err) => {
        if (!ativo) return;
        toast.error(extrairMensagemErro(err));
      });
    return () => { ativo = false; };
  }, [intercambioAtivo, filtros.codigoGrupoEmpresa]);

  const recalcularJuros = useCallback(async (codigos, dataBaixaRef, considerarJurosRef) => {
    if (!codigos || codigos.length === 0) {
      setCalculados(new Map());
      return;
    }
    setCalculandoJuros(true);
    try {
      const payload = {
        codigos,
        dataBaixa: dataBaixaRef,
        considerarJuros: considerarJurosRef,
      };
      const res = await calcularJurosBaixa(payload);
      const lista = normalizarResposta(res) || [];
      const mapa = new Map();
      lista.forEach((item) => {
        const codigo = item.codigo_titulo;
        mapa.set(codigo, {
          valor: Number(item.valor || 0),
          juros: Number(item.juros || 0),
          multa: Number(item.multa || 0),
          total: Number(item.total || 0),
        });
      });
      setCalculados(mapa);
    } catch (err) {
      toast.error(extrairMensagemErro(err));
    } finally {
      setCalculandoJuros(false);
    }
  }, []);

  useEffect(() => {
    const codigos = titulos.map((t) => t.codigo_titulo);
    if (codigos.length === 0) {
      setCalculados(new Map());
      return;
    }
    recalcularJuros(codigos, dataBaixa, considerarJuros);
  }, [titulos, dataBaixa, considerarJuros, recalcularJuros]);

  const bancosOptionsBaixa = useMemo(
    () =>
      (bancos || []).map((b) => ({
        value: b.codigo,
        label: b.descricao || b.apelido || String(b.codigo),
      })),
    [bancos],
  );

  const totais = useMemo(() => {
    let totalValor = 0;
    let totalGeral = 0;
    selecao.forEach((codigo) => {
      const calc = calculados.get(codigo);
      if (calc) {
        totalValor += calc.valor;
        totalGeral += calc.total;
      }
    });
    return {
      totalValor,
      quantidade: selecao.size,
      totalGeral,
    };
  }, [selecao, calculados]);

  const handlePesquisar = async (pagina = 1) => {
    try {
      await filtrosSchema.validate({
        codigoGrupoEmpresa: filtros.codigoGrupoEmpresa,
        dataVencimentoIni: filtros.dataVencimentoIni,
        dataVencimentoFim: filtros.dataVencimentoFim,
      });
    } catch (err) {
      toast.error(err.message || 'Verifique os filtros obrigatórios');
      return;
    }
    setCarregando(true);
    try {
      const res = await listarTitulosBaixa(construirPayloadListar(filtros, pagina, sortConfig));
      const dados = normalizarResposta(res) || {};
      setTitulos(dados.titulos || []);
      setTotalRegistros(dados.total || 0);
      setPaginaAtual(dados.pagina || pagina);
      setSelecao(new Set());
    } catch (err) {
      toast.error(extrairMensagemErro(err));
    } finally {
      setCarregando(false);
    }
  };

  const handleMudarPagina = (novaPagina) => {
    setPaginaAtual(novaPagina);
    handlePesquisar(novaPagina);
  };

  const handleMudarSort = (campo) => {
    const novaDirecao = sortConfig.campo === campo && sortConfig.direcao === 'ASC' ? 'DESC' : 'ASC';
    const novo = { campo, direcao: novaDirecao };
    setSortConfig(novo);
    setPaginaAtual(1);
    listarTitulosBaixa(construirPayloadListar(filtros, 1, novo))
      .then((res) => {
        const dados = normalizarResposta(res) || {};
        setTitulos(dados.titulos || []);
        setTotalRegistros(dados.total || 0);
        setSelecao(new Set());
      })
      .catch((err) => toast.error(extrairMensagemErro(err)));
  };

  const handleLimpar = () => {
    const base = filtrosIniciais();
    // mantém GE auto-selecionado
    base.codigoGrupoEmpresa = filtros.codigoGrupoEmpresa;
    setFiltros(base);
    setTitulos([]);
    setSelecao(new Set());
    setTotalRegistros(0);
    setPaginaAtual(1);
  };

  const handleToggleSelecao = (codigoTitulo) => {
    setSelecao((prev) => {
      const proximo = new Set(prev);
      if (proximo.has(codigoTitulo)) {
        proximo.delete(codigoTitulo);
      } else if (proximo.size < LIMITE_LOTE_TITULOS) {
        proximo.add(codigoTitulo);
      } else {
        toast.warn(`Limite de ${LIMITE_LOTE_TITULOS} títulos por lote atingido`);
      }
      return proximo;
    });
  };

  const handleToggleSelecaoTodos = (marcarTodos) => {
    setSelecao((prev) => {
      const proximo = new Set(prev);
      if (marcarTodos) {
        for (const t of titulos) {
          if (proximo.size >= LIMITE_LOTE_TITULOS) break;
          proximo.add(t.codigo_titulo);
        }
      } else {
        titulos.forEach((t) => proximo.delete(t.codigo_titulo));
      }
      return proximo;
    });
  };

  const construirPayloadBaixa = () => {
    const intercambioPayload = intercambioAtivo
      ? {
          codigoContaCredito: Number(intercambio.codigoContaCredito),
          codigoContaDebito: Number(intercambio.codigoContaDebito),
          codigoDepartamento: Number(intercambio.codigoDepartamento),
        }
      : null;
    return {
      codigoGrupoEmpresa: Number(filtros.codigoGrupoEmpresa),
      dataBaixa,
      codigoBanco: Number(codigoBancoBaixa),
      considerarJuros,
      codigosTitulos: Array.from(selecao),
      intercambio: intercambioPayload,
      dataContabil: chContabil ? dataContabil : null,
      chContabil,
    };
  };

  const handleAbrirConfirmacao = async () => {
    const payload = construirPayloadBaixa();
    try {
      await baixaSchema.validate(payload, { abortEarly: true });
    } catch (err) {
      toast.error(err.message || 'Revise os campos obrigatórios da baixa');
      return;
    }
    if (intercambioAtivo) {
      const algumPreenchido =
        intercambio.codigoContaCredito ||
        intercambio.codigoContaDebito ||
        intercambio.codigoDepartamento;
      const todosPreenchidos =
        intercambio.codigoContaCredito &&
        intercambio.codigoContaDebito &&
        intercambio.codigoDepartamento;
      if (algumPreenchido && !todosPreenchidos) {
        toast.error('Preencha todos os 3 campos do Intercâmbio (Conta Crédito, Conta Débito e Departamento)');
        return;
      }
    }
    setModalConfirmacao(true);
  };

  const handleConfirmarBaixa = async () => {
    setModalConfirmacao(false);
    setProcessandoBaixa(true);
    try {
      const payload = construirPayloadBaixa();
      const res = await processarBaixaTitulo(payload);
      const dados = normalizarResposta(res) || {};
      toast.success(
        `Baixa concluída: ${dados.titulos_baixados || selecao.size} título(s), total ${(
          dados.valor_total || totais.totalGeral
        ).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`,
      );
      setSelecao(new Set());
      // Re-fetch pra refletir nova lista; se ficou vazio na última página, volta uma
      const totalEstimado = totalRegistros - (dados.titulos_baixados || selecao.size);
      const ultimaPagina = Math.max(1, Math.ceil(totalEstimado / TAMANHO_PAGINA));
      const proximaPagina = Math.min(paginaAtual, ultimaPagina);
      handlePesquisar(proximaPagina);
    } catch (err) {
      toast.error(extrairMensagemErro(err));
    } finally {
      setProcessandoBaixa(false);
    }
  };

  return (
    <Container fluid className="py-3">
      <Header title="Baixa Título por Data" />
      {(carregando || processandoBaixa) && <Loading />}

      <CardFiltros
        filtros={filtros}
        onChange={setFiltros}
        onPesquisar={() => handlePesquisar(1)}
        onLimpar={handleLimpar}
        gruposEmpresa={gruposEmpresa}
        bancos={bancos}
        portadores={portadores}
        cedentes={cedentes}
        moedas={moedas}
        geAutoSelecionado={gruposEmpresa.length === 1}
      />

      <PainelTotais totais={totais} carregandoJuros={calculandoJuros} />

      <GradeTitulos
        titulos={titulos}
        calculados={calculados}
        selecao={selecao}
        onToggle={handleToggleSelecao}
        onToggleTodos={handleToggleSelecaoTodos}
        sortConfig={sortConfig}
        onChangeSort={handleMudarSort}
        paginaAtual={paginaAtual}
        totalRegistros={totalRegistros}
        tamanhoPagina={TAMANHO_PAGINA}
        onMudarPagina={handleMudarPagina}
        limiteLote={LIMITE_LOTE_TITULOS}
      />

      <div className="bg-white border rounded p-3 mt-3">
        <h5 className="mb-3">Dados da baixa</h5>
        <Row className="align-items-end">
          <Col xs={12} md={3}>
            <Form.Group>
              <Form.Label className="small mb-1">Data da baixa *</Form.Label>
              <Form.Control
                type="date"
                value={dataBaixa}
                onChange={(e) => setDataBaixa(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={3}>
            <Form.Group>
              <Form.Label className="small mb-1">Banco (baixa) *</Form.Label>
              <Select
                styles={customStyles}
                components={{ DropdownIndicator }}
                options={bancosOptionsBaixa}
                placeholder="Selecione..."
                noOptionsMessage={() => 'Nenhum banco'}
                value={bancosOptionsBaixa.find((o) => o.value === codigoBancoBaixa) || null}
                onChange={(opt) => setCodigoBancoBaixa(opt ? opt.value : null)}
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={3}>
            <Form.Label className="small mb-1 d-block">Considerar juros/multa</Form.Label>
            <Form.Check
              type="checkbox"
              id="ch-considerar-juros"
              label="Aplicar juros e multa"
              checked={considerarJuros}
              onChange={(e) => setConsiderarJuros(e.target.checked)}
            />
          </Col>
          <Col xs={12} md={3}>
            <Form.Label className="small mb-1 d-block">Movimento contábil</Form.Label>
            <Form.Check
              type="checkbox"
              id="ch-mov-contabil"
              label="Gerar movimento contábil"
              checked={chContabil}
              onChange={(e) => setChContabil(e.target.checked)}
            />
            {chContabil && (
              <Form.Control
                type="date"
                className="mt-2"
                value={dataContabil}
                onChange={(e) => setDataContabil(e.target.value)}
              />
            )}
          </Col>
        </Row>
      </div>

      <IntercambioFields
        ativo={intercambioAtivo}
        onToggle={setIntercambioAtivo}
        intercambio={intercambio}
        onChange={setIntercambio}
        cedentes={cedentes}
        contas={cedentes}
        departamentos={departamentos}
      />

      <div className="d-flex justify-content-end mt-3">
        <Button
          variant="success"
          size="lg"
          disabled={processandoBaixa || selecao.size === 0}
          onClick={handleAbrirConfirmacao}
        >
          {processandoBaixa ? 'Processando...' : `Baixar ${selecao.size} título(s)`}
        </Button>
      </div>

      <ConfirmacaoBaixaModal
        show={modalConfirmacao}
        onHide={() => setModalConfirmacao(false)}
        onConfirmar={handleConfirmarBaixa}
        quantidade={selecao.size}
        totalGeral={totais.totalGeral}
        intercambioAtivo={intercambioAtivo}
        dataBaixa={dataBaixa}
      />
    </Container>
  );
};

export default BaixaPorData;
